
import React, { useState, useMemo, useEffect } from 'react';
import { findHelpGuide, getHelpLibraryContent, searchHelpContent, type HelpLibraryAccess } from '../../services/helpLibraryContent';
import type { ManualSection } from '@types';
import { createSafeMarkdown } from '../utils/safeMarkdown';
import type { HelpRequest } from '../App';
import { helpMarkdownClassName } from '../common/helpStyles';

const md = createSafeMarkdown({ html: true });

interface HelpLibraryProps extends HelpLibraryAccess {
    onClose: () => void;
    helpContext?: HelpRequest | null;
}

const HelpLibrary: React.FC<HelpLibraryProps> = ({ onClose, helpContext, fullWorkspaceAccess = false, userRole, serviceRequestAccess = false }) => {
    const allContent = useMemo(() => getHelpLibraryContent({ fullWorkspaceAccess, userRole, serviceRequestAccess }), [fullWorkspaceAccess, userRole, serviceRequestAccess]);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeKey, setActiveKey] = useState<string | null>(() => {
        const guide = findHelpGuide(allContent, helpContext?.workflowName, helpContext?.suiteName);
        return allContent.find(([, item]) => item === guide)?.[0] || 'instructionManual';
    });
    const activeContent = activeKey ? (allContent.find(([key]) => key === activeKey)?.[1] || allContent[0][1]) : null;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Block Ctrl+P (Print), Ctrl+S (Save), Ctrl+C/X (Copy/Cut), Ctrl+U (Source), F12 (Dev Tools)
      if (
        (e.ctrlKey && (e.key === 'p' || e.key === 's' || e.key === 'c' || e.key === 'x' || e.key === 'u')) ||
        e.key === 'F12'
      ) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

    useEffect(() => {
        const guide = findHelpGuide(allContent, helpContext?.workflowName, helpContext?.suiteName);
        setActiveKey(allContent.find(([, item]) => item === guide)?.[0] || 'instructionManual');
    }, [helpContext, allContent]);

    const filteredContent = useMemo(() => searchHelpContent(allContent, searchTerm), [searchTerm, allContent]);

  const renderSection = (section: ManualSection, index: number) => {
    const renderedHtml = md.render(section.content);
    return (
        <div key={index} className="mb-6 prose prose-slate max-w-none relative z-10">
          <h3 className="text-xl font-semibold mb-2 text-slate-800">{section.title}</h3>
          <div className={helpMarkdownClassName} dangerouslySetInnerHTML={{ __html: renderedHtml }} />
        </div>
    );
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4 select-none print:hidden"
      onContextMenu={(e) => e.preventDefault()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="help-library-title"
    >
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .no-print { display: none !important; }
        }
        .watermark {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 5;
          opacity: 0.05;
          overflow: hidden;
          user-select: none;
        }
        .watermark-text {
          transform: rotate(-45deg);
          font-size: 5rem;
          font-weight: bold;
          white-space: nowrap;
          color: #64748b;
        }
      `}</style>

      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl h-[90vh] flex flex-col relative overflow-hidden no-print">
        {/* Watermark Overlay */}
        <div className="watermark" aria-hidden="true">
          <div className="watermark-text">
            INTELOPTICS PROPRIETARY • CONFIDENTIAL • DO NOT DISTRIBUTE
          </div>
        </div>

        <header className="flex items-center justify-between p-4 border-b bg-slate-50 rounded-t-lg relative z-20">
          <h2 id="help-library-title" className="text-xl sm:text-2xl font-bold text-slate-800">{fullWorkspaceAccess ? 'Admin' : 'Client'} Help & Guidance Library</h2>
          <button onClick={onClose} className="shrink-0 p-2 rounded-full hover:bg-slate-200" aria-label="Close help library">
             <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </header>

        <div className="flex-grow min-h-0 flex flex-col md:flex-row overflow-hidden relative z-20">
          {/* Sidebar */}
          <nav className="w-full md:w-1/3 shrink-0 max-h-[35%] md:max-h-none border-b md:border-b-0 md:border-r overflow-y-auto p-4 bg-slate-light">
            <p className="mb-3 text-sm text-slate-600">{fullWorkspaceAccess ? 'Steps for managing clients, preparing work, and publishing delivery.' : 'Steps for using your client screens and delivered services.'}</p>
            <label htmlFor="help-library-search" className="sr-only">Search instructions</label>
            <input
              type="search"
              name="help-library-search"
              id="help-library-search"
              placeholder="Search instructions..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setActiveKey(null); }}
              className="w-full p-2 border border-slate-300 rounded-md mb-4 sticky top-0 bg-white"
            />
            <ul>
              {filteredContent.map(([key, guide]) => (
                <li key={key} className="mb-1">
                  <button
                    onClick={() => setActiveKey(key)}
                    aria-current={activeContent === guide ? 'page' : undefined}
                    className={`w-full text-left p-2 rounded-md text-sm ${
                      activeContent === guide
                        ? 'bg-blue-500 text-white' 
                        : 'hover:bg-slate-200'
                    }`}
                  >
                    {guide.title}
                  </button>
                </li>
              ))}
            </ul>
            {!filteredContent.length && <p role="status" className="mt-4 text-sm text-slate-500">No instructions found. Try a screen name or a shorter search.</p>}
          </nav>

          {/* Main Content */}
          <main className="w-full md:w-2/3 min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
            {activeContent ? (
              <div className="relative">
                <h2 className="text-2xl sm:text-3xl font-bold mb-3 text-slate-900">{activeContent.title}</h2>
                <p className="mb-6 text-slate-600 italic">{activeContent.introduction}</p>
                {activeContent.sections.map(renderSection)}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-slate-500">
                <p>Select a topic from the list or use the search bar to find help.</p>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default HelpLibrary;
